import { Box, Container, Hidden, Paper, Typography } from '@mui/material'
import { useRouter } from 'next/navigation'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { GrayButton } from '~/components/GrayButton'
import { LoadingBackdrop } from '~/components/LoadingBackdrop'
import { PageType } from '~/features/profile/components/Withdrawal/PageTypeProvider/context'
import { WithdrawalBegin } from '~/features/profile/components/Withdrawal/WithdrawalBegin'
import { WithdrawalComplete } from '~/features/profile/components/Withdrawal/WithdrawalComplete'
import { WithdrawalConfirm } from '~/features/profile/components/Withdrawal/WithdrawalConfirm'
import { WithdrawalSelectReason } from '~/features/profile/components/Withdrawal/WithdrawalSelectReason'
import { useWithdrawalFormHandler } from '~/features/profile/hooks/withdrawal/useWithdrawalFormHandler/useWithdrawalFormHandler'
import { useWithdrawalInfo } from '~/features/profile/hooks/withdrawal/useWithdrawalInfo'
import * as GA from '~/features/profile/libs/ga'
import { URLConstants } from '~/libs/constants'
import { Colors } from '~/libs/mui/theme'

interface WithdrawalPageContentProps {
  pageType: PageType
  updatePageType: (nextPageType: PageType) => void
  onClickPageBack?: () => void
}

export function WithdrawalPageContent({
  pageType,
  updatePageType,
  onClickPageBack,
}: WithdrawalPageContentProps): JSX.Element {
  const { push, replace } = useRouter()
  const withdrawalInfo = useWithdrawalInfo()
  const {
    formState,
    withdrawalResponse,
    isValidated,
    handleChangeSelectedReason,
    handleChangeFreeTextReason,
    handleWithdrawalSubmit,
  } = useWithdrawalFormHandler()
  const { t } = useTranslation()

  useEffect(() => {
    if (
      typeof withdrawalInfo !== 'string' &&
      withdrawalInfo.profile.user_status === 'withdrawal_user'
    ) {
      // すでに解約済みならリダイレクト
      void replace('/profile')
    }
  }, [withdrawalInfo, replace])

  if (withdrawalInfo === 'error') {
    throw new Error(t('error_occurred'))
  }

  if (withdrawalInfo === 'loading' || withdrawalResponse.isLoading) {
    return <LoadingBackdrop />
  }

  return (
    <Container maxWidth='sm' disableGutters>
      <Paper
        sx={{
          paddingY: { xs: 4, sm: 8 },
          paddingX: '36px',
          minHeight: { xs: 'calc(100vh - 112px)', sm: 'calc(100vh - 164px)' },
          position: 'relative',
        }}
      >
        <Hidden smDown>
          <Typography variant='h1' textAlign='center' color={Colors.yellow} marginBottom={4}>
            {t('withdrawal')}
          </Typography>
        </Hidden>
        {pageType === 'begin' && (
          <WithdrawalBegin
            videos={withdrawalInfo.videos}
            onClickVideo={(video) => {
              GA.WithdrawalBeginClickVideo(video.id)
              void push(`/videos/${video.id}/play`)
            }}
            onClickProceed={() => {
              GA.WithdrawalBeginClickProceed()
              updatePageType('select_reason')
            }}
            onClickCancel={() => {
              GA.WithdrawalBeginClickCancel()
              void push('/profile')
            }}
          />
        )}
        {pageType === 'select_reason' && (
          <WithdrawalSelectReason
            defaultSelectedReason={formState.selectReason}
            isValidated={isValidated}
            onChangeSelectedReason={handleChangeSelectedReason}
            reasons={withdrawalInfo.reason.withdrawal_reasons}
            onClickProceed={() => {
              GA.WithdrawalSelectReasonClickProceed()
              updatePageType('confirm')
            }}
            onClickCancel={() => {
              GA.WithdrawalSelectReasonClickCancel()
              void push('/profile')
            }}
          />
        )}
        {pageType === 'confirm' && (
          <WithdrawalConfirm
            defaultInputText={formState.freeTextReason}
            error={withdrawalResponse.error}
            onChangeFreeTextReason={handleChangeFreeTextReason}
            onSubmit={() => {
              handleWithdrawalSubmit()
              GA.WithdrawalConfirmSubmit()
              updatePageType('complete')
            }}
            onClickCancel={() => {
              GA.WithdrawalConfirmCancel()
              void push('/profile')
            }}
          />
        )}
        {pageType === 'complete' && (
          <WithdrawalComplete
            user={withdrawalResponse.deletedUserInfo}
            onClickAppStore={(isIos) => {
              GA.WithdrawalCompleteStore(isIos ? 'appstore' : 'googleplay')
              window.open(
                isIos ? URLConstants.subscriptionsIniOS : URLConstants.subscriptionsInAndroid,
              )
            }}
            onClickLogout={() => {
              GA.WithdrawalCompleteLogout()
              void push('/logout')
            }}
          />
        )}
        {onClickPageBack && (
          <Box sx={{ display: { xs: 'none', sm: 'block' } }} position='absolute' top={18} left={18}>
            <GrayButton onClick={onClickPageBack}>{t('back')}</GrayButton>
          </Box>
        )}
      </Paper>
    </Container>
  )
}
